import { memo, useEffect, useState } from 'react';
import { FileIcon, FilePlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ChatDocumentDTO, ChatSourceReferenceDTO } from '@shared/schemas/chat';
import { chunkAnchorSchema, type ChunkAnchor } from '@shared/schemas/rag-anchors';
import {
  Message,
  MessageContent,
  MessageResponse,
  MessageToolbar,
} from '@/components/ai-elements/message';
import { Shimmer } from '@/components/ai-elements/shimmer';
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@/components/ai-elements/reasoning';
import {
  Tool,
  ToolHeader,
  type ToolPart,
} from '@/components/ai-elements/tool';
import { useChatExperience, type ActiveSourceReference } from '../chat-experience';
import { useStreamRevealText } from '../hooks/useStreamRevealText';
import { ChatDocumentCard } from './ChatDocumentCard';
import { ChatAskUserTool } from './ChatAskUserTool';
import { ChatDocumentGenerationTool } from './ChatDocumentGenerationTool';
import { getAskUserToolParts, sanitizeAssistantTextDisplay } from '../chat-ask-user-flow';
import { collectWebCitationSources, linkifyWebCitations } from '../chat-web-citations';
import { ChatMessageActions } from './ChatMessageActions';
import { ChatSourceCard } from './ChatSourceCard';
import { ChatWebActivity } from './ChatWebActivity';
import { getChatToolDisplayName } from '../chat-tool-labels';
import type { ChatUIMessage, ChatUIFilePart } from '../types';
import { Button } from '@/components/ui/button';
import {
  Source,
} from '@/components/ai-elements/sources';
import { apiBaseUrl } from '@/lib/auth-client';
import { deriveAssistantVisualState } from '../assistant-visual-state';
import {
  getSafeToolStatusMessage,
  resolveChatToolRenderer,
} from '../chat-tool-renderer-registry';
import {
  countChatUxEvent,
  markChatAssistantActivityVisible,
  markChatFirstTextVisible,
} from '../chat-ux-metrics';
import { areMessageBubblePropsEqual } from './message-bubble-memo';

export type MessageBubbleProps = {
  canRetry: boolean;
  isLoading?: boolean;
  message: ChatUIMessage;
  onEditMessage: (message: ChatUIMessage) => void;
  onRetryAssistant: () => void;
  /** Horodatage du début de tour (durée Reasoning stable cross-remount). */
  reasoningStartedAt?: number;
};

type ChatPart = ChatUIMessage['parts'][number];
type TextPart = Extract<ChatPart, { type: 'text' }>;
type ReasoningPart = Extract<ChatPart, { type: 'reasoning' }>;
type SourcePart = Extract<ChatPart, { type: 'source-document' | 'source-url' }>;
type WebUrlSourcePart = Extract<ChatPart, { type: 'source-url' }>;
type DocumentPart = Extract<ChatPart, {
  type: 'data-document-finish' | 'data-document-open' | 'data-document-preview';
}>;
type RagEmptyPart = Extract<ChatPart, { type: 'data-rag-empty' }>;
type WebResearchActivityPart = Extract<ChatPart, { type: 'data-web-research-activity' }>;
type ToolDisplayPart = ChatPart & {
  errorText?: string;
  input?: unknown;
  output?: unknown;
  state: ToolPart['state'];
  title?: string;
  toolName?: string;
};

function getTextParts(message: ChatUIMessage) {
  const textParts = message.parts.filter((part): part is TextPart => part.type === 'text');
  // Une seule voix : la boucle web multi-steps ré-émet du texte à chaque step
  // pendant le stream, mais le backend ne persiste que la dernière part texte
  // (mapUiToolPartsToStoredParts). Le rendu assistant suit la même règle — le
  // texte courant remplace le précédent au lieu de s'y empiler.
  if (message.role === 'assistant' && textParts.length > 1) {
    return [textParts[textParts.length - 1]!];
  }
  return textParts;
}

function getReasoningParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is ReasoningPart => part.type === 'reasoning');
}

function getFileParts(message: ChatUIMessage): ChatUIFilePart[] {
  return message.parts
    .filter((part) => part.type === 'file')
    .map((part) => part as unknown as ChatUIFilePart);
}

function getStreamedRagSourceParts(message: ChatUIMessage): SourcePart[] {
  return message.parts.flatMap((part) => {
    if (part.type !== 'data-rag-citations') {
      return [];
    }

    return part.data.citations.map((citation) => ({
      ...citation,
      type: 'source-document' as const,
    }));
  });
}

function getSourceParts(message: ChatUIMessage, includeStreamedRagSources: boolean) {
  const persistedSourceParts = message.parts.filter((part): part is SourcePart => (
    part.type === 'source-document' || part.type === 'source-url'
  ));

  if (persistedSourceParts.length > 0) {
    return persistedSourceParts;
  }

  return includeStreamedRagSources ? getStreamedRagSourceParts(message) : [];
}

function getWebUrlSourceParts(message: ChatUIMessage, includeStreamedRagSources: boolean) {
  return getSourceParts(message, includeStreamedRagSources)
    .filter((part): part is WebUrlSourcePart => part.type === 'source-url');
}

function getDocumentSourceParts(message: ChatUIMessage, includeStreamedRagSources: boolean) {
  return getSourceParts(message, includeStreamedRagSources)
    .filter((part): part is Extract<SourcePart, { type: 'source-document' }> => (
      part.type === 'source-document'
    ));
}

function getDocumentParts(message: ChatUIMessage) {
  const partsByDocumentId = new Map<string, DocumentPart>();

  message.parts
    .filter((part): part is DocumentPart => (
    part.type === 'data-document-finish'
    || part.type === 'data-document-open'
    || part.type === 'data-document-preview'
    ))
    .forEach((part) => {
      const current = partsByDocumentId.get(part.data.documentId);

      if (!current || getDocumentPartPriority(part) >= getDocumentPartPriority(current)) {
        partsByDocumentId.set(part.data.documentId, part);
      }
    });

  return Array.from(partsByDocumentId.values());
}

function getRagEmptyParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is RagEmptyPart => part.type === 'data-rag-empty');
}

function getWebResearchActivityParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is WebResearchActivityPart => (
    part.type === 'data-web-research-activity'
  ));
}

type DocgenPendingPart = Extract<ChatPart, { type: 'data-document-generation-pending' }>;

function getDocgenPendingParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is DocgenPendingPart => (
    part.type === 'data-document-generation-pending'
  ));
}

function getToolParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is ToolDisplayPart => (
    (part.type === 'dynamic-tool' || part.type.startsWith('tool-'))
    && 'state' in part
  ));
}

function getRawToolName(part: ToolDisplayPart) {
  return part.type === 'dynamic-tool'
    ? part.toolName ?? ''
    : part.type.replace(/^tool-/, '');
}

function getVisibleToolParts(message: ChatUIMessage) {
  return getToolParts(message).filter((part) => {
    const rawName = getRawToolName(part);
    const renderer = resolveChatToolRenderer(rawName);
    return renderer !== 'hidden' && renderer !== 'ask-user';
  });
}

function getDocumentPartPriority(part: DocumentPart) {
  if (part.data.status === 'ready' || part.data.status === 'error') {
    return 3;
  }

  if (part.type === 'data-document-finish') {
    return 2;
  }

  return 1;
}

function getToolName(part: ToolDisplayPart) {
  const rawName = part.type === 'dynamic-tool'
    ? part.toolName ?? 'outil'
    : part.type.replace(/^tool-/, '') || 'outil';

  return getChatToolDisplayName(rawName);
}

function getSourceCitationPage(part: SourcePart) {
  if (part.type !== 'source-document') {
    return null;
  }

  const value = (part as Record<string, unknown>).citationPage;
  return typeof value === 'number' ? value : null;
}

function getSourceExcerpt(part: SourcePart) {
  if (part.type !== 'source-document') {
    return null;
  }

  const value = (part as Record<string, unknown>).excerpt;
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function getSourceUrl(part: SourcePart) {
  if (part.type === 'source-url') {
    return part.url;
  }

  const value = (part as Record<string, unknown>).url;
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function getSourceAnchor(part: SourcePart): ChunkAnchor | null {
  if (part.type !== 'source-document') {
    return null;
  }

  const result = chunkAnchorSchema.safeParse((part as Record<string, unknown>).anchor ?? null);
  return result.success ? result.data : null;
}

function resolveSourceReferencePage(part: SourcePart, anchor: ChunkAnchor | null) {
  const citationPage = getSourceCitationPage(part);
  if (citationPage && citationPage > 0) {
    return citationPage;
  }

  if (!anchor) {
    return null;
  }

  if (anchor.kind === 'pdf') {
    return anchor.page;
  }

  if (anchor.kind === 'pptx') {
    return anchor.slide;
  }

  return 1;
}

function toReferenceAnchor(reference: ChatSourceReferenceDTO, partAnchor: ChunkAnchor | null): ChunkAnchor | null {
  const referenceAnchor = chunkAnchorSchema.safeParse(reference.anchor ?? null);
  if (referenceAnchor.success && referenceAnchor.data) {
    return referenceAnchor.data;
  }

  if (partAnchor?.kind === 'pdf') {
    return {
      bbox: partAnchor.page === reference.page ? partAnchor.bbox : null,
      kind: 'pdf',
      page: reference.page,
    };
  }

  if (partAnchor?.kind === 'pptx') {
    return {
      kind: 'pptx',
      shapeId: partAnchor.shapeId,
      slide: reference.page,
    };
  }

  return partAnchor;
}

function toActiveSourceReference(reference: ChatSourceReferenceDTO, anchor: ChunkAnchor | null = null): ActiveSourceReference {
  return {
    anchor,
    blockId: reference.blockId,
    chunkRerankScore: reference.chunkRerankScore,
    endLine: reference.endLine,
    excerpt: reference.excerpt,
    isVerifiable: reference.isVerifiable,
    page: reference.page,
    referenceId: reference.referenceId,
    sectionPath: reference.sectionPath,
    source: reference.source,
    startLine: reference.startLine,
  };
}

function getSourceReferences(part: SourcePart): ActiveSourceReference[] {
  if (part.type !== 'source-document') {
    return [];
  }

  const sourceReferences = ((part as Record<string, unknown>).references ?? null);
  if (Array.isArray(sourceReferences)) {
    const anchor = getSourceAnchor(part);
    const parsed = sourceReferences
      .map((reference) => {
        if (!reference || typeof reference !== 'object') {
          return null;
        }

        const value = reference as ChatSourceReferenceDTO;
        if (!value.referenceId || typeof value.page !== 'number' || value.page < 1) {
          return null;
        }

        return toActiveSourceReference(value, toReferenceAnchor(value, anchor));
      })
      .filter((reference): reference is ActiveSourceReference => Boolean(reference));

    if (parsed.length > 0) {
      return parsed.slice(0, 12);
    }
  }

  const excerpt = getSourceExcerpt(part);
  const anchor = getSourceAnchor(part);
  const citationPage = resolveSourceReferencePage(part, anchor);
  if (!excerpt || !citationPage || citationPage < 1) {
    return [];
  }

  return [{
    anchor,
    excerpt,
    page: citationPage,
    referenceId: ((part as Record<string, unknown>).citationBlockId as string | undefined) ?? `${part.sourceId}:fallback`,
  }];
}

function shouldRenderSourceArtifact(part: SourcePart) {
  if (part.type === 'source-url') {
    return true;
  }

  const referencesValue = (part as Record<string, unknown>).references;
  const hasReferences = Array.isArray(referencesValue) && referencesValue.length > 0;
  const hasExcerpt = Boolean(getSourceExcerpt(part));
  const hasCitationPage = typeof getSourceCitationPage(part) === 'number';

  return hasReferences || hasExcerpt || hasCitationPage;
}

function toCitationDocument(part: SourcePart): ChatDocumentDTO {
  const title = part.title ?? 'Source documentaire';
  const filename = part.type === 'source-document' ? (part.filename ?? null) : null;
  const excerpt = getSourceExcerpt(part);
  const anchor = getSourceAnchor(part);
  const citationPageValue = part.type === 'source-document'
    ? resolveSourceReferencePage(part, anchor)
    : null;
  const citationPage = typeof citationPageValue === 'number'
    ? `Page ${citationPageValue}`
    : null;
  const citationDocumentIdValue = part.type === 'source-document'
    ? (part as Record<string, unknown>).citationDocumentId
    : null;
  const citationDocumentId = typeof citationDocumentIdValue === 'string' && citationDocumentIdValue.trim().length > 0
    ? citationDocumentIdValue
    : null;
  const sourceUrl = citationDocumentId
    ? new URL(`/api/documents/${citationDocumentId}/content`, apiBaseUrl).toString()
    : getSourceUrl(part);
  const content = [
    `## ${title}`,
    citationPage ? `- ${citationPage}` : null,
    filename && filename !== title ? `- Document: ${filename}` : null,
    part.type === 'source-url' ? `- URL: ${part.url}` : null,
    excerpt ? `\n${excerpt}` : '\nAperçu non disponible.',
  ]
    .filter(Boolean)
    .join('\n');

  return {
    content,
    documentId: citationDocumentId ?? `rag-citation-${part.sourceId}`,
    filename,
    kind: 'attachment' as const,
    mediaType: part.type === 'source-document' ? (part.mediaType ?? 'application/pdf') : 'text/uri-list',
    sourceUrl,
    status: 'ready' as const,
    summary: excerpt?.slice(0, 180) ?? title,
    title,
  };
}

function MessageBubbleComponent({
  canRetry,
  isLoading,
  message,
  onEditMessage,
  onRetryAssistant,
  reasoningStartedAt,
}: MessageBubbleProps) {
  const { openDocument } = useChatExperience();
  const [areSourcesVisible, setAreSourcesVisible] = useState(false);
  const textParts = getTextParts(message);
  const reasoningParts = getReasoningParts(message);
  const fileParts = getFileParts(message);
  const includeStreamedRagSources = !isLoading;
  const sourceParts = getDocumentSourceParts(message, includeStreamedRagSources);
  const redactedSourceParts = sourceParts.filter((part) => (
    (part as Record<string, unknown>).accessStatus === 'redacted'
  ));
  const webUrlSourceParts = getWebUrlSourceParts(message, includeStreamedRagSources);
  const documentParts = getDocumentParts(message);
  const ragEmptyParts = getRagEmptyParts(message);
  const webResearchActivityParts = getWebResearchActivityParts(message);
  const docgenPendingParts = getDocgenPendingParts(message);
  const askUserToolParts = (() => {
    const resolvedParts = getAskUserToolParts(message);
    const pendingParts = resolvedParts.filter((part) => (
      part.state === 'input-available' || part.state === 'input-streaming'
    ));

    if (pendingParts.length === 0) {
      return resolvedParts;
    }

    return [
      ...resolvedParts.filter((part) => (
        part.state !== 'input-available' && part.state !== 'input-streaming'
      )),
      pendingParts.at(-1)!,
    ];
  })();
  const toolParts = getVisibleToolParts(message);
  const webActivityToolParts = toolParts.filter((part) => (
    resolveChatToolRenderer(getRawToolName(part)) === 'web-activity'
  ));
  // La carte de génération n'est rendue qu'une fois le tool call RÉSOLU. Tant que
  // l'input est en cours de streaming, son schéma ne parse pas et le titre retombe
  // sur « Document riche » : l'utilisateur voyait apparaître un artefact générique
  // qui changeait ensuite d'identité. On garde le texte de préparation jusque-là.
  const documentGenerationToolParts = toolParts.filter((part) => (
    resolveChatToolRenderer(getRawToolName(part)) === 'document-generation'
    && part.state !== 'input-streaming'
    && part.state !== 'input-available'
  ));
  const genericToolParts = toolParts.filter((part) => (
    resolveChatToolRenderer(getRawToolName(part)) === 'safe-fallback'
  ));
  const isUser = message.role === 'user';
  const rawTextContent = sanitizeAssistantTextDisplay(
    textParts.map((part) => part.text).join('\n'),
  );
  const textContent = isUser
    ? rawTextContent
    : linkifyWebCitations(
      rawTextContent,
      collectWebCitationSources(message.parts as ReadonlyArray<{ type: string } & Record<string, unknown>>),
    );
  const reasoningContent = reasoningParts.map((part) => part.text).join('\n\n').trim();
  // Pacer de reveal : le texte assistant entre dans le DOM à cadence bornée et
  // monotone (l'ordre visuel est garanti par construction — le fondu Streamdown
  // reste sans stagger). Inactif sur les bulles user et l'historique.
  const { isRevealing, text: revealedTextContent } = useStreamRevealText(
    isUser ? '' : textContent,
    Boolean(isLoading && !isUser),
  );
  // SOTA AI Elements : le panel reasoning ne reste "streaming" que tant que
  // la dernière part du message est encore du reasoning (fermeture auto dès
  // que le texte / tools démarrent).
  const isReasoningStreaming = Boolean(
    isLoading && message.parts.at(-1)?.type === 'reasoning',
  );
  const visualState = deriveAssistantVisualState({
    message,
    status: isLoading ? 'streaming' : 'ready',
  });
  // Une carte par document source. Le backend regroupe et filtre déjà la
  // pertinence pour les nouveaux messages ; ce dédoublonnage par document côté
  // frontend reste un filet de sécurité pour les conversations déjà persistées
  // (où chaque chunk était une part `source-document` distincte).
  const sourceArtifactsByDocument = new Map<string, {
    document: ChatDocumentDTO;
    references: ActiveSourceReference[];
  }>();

  for (const part of sourceParts.filter((candidate) => shouldRenderSourceArtifact(candidate))) {
    const references = getSourceReferences(part);
    if (references.length === 0) {
      continue;
    }

    const citationDocumentId = (part as Record<string, unknown>).citationDocumentId;
    const documentKey = typeof citationDocumentId === 'string' && citationDocumentId.trim().length > 0
      ? citationDocumentId
      : part.sourceId;

    const existing = sourceArtifactsByDocument.get(documentKey);
    if (existing) {
      existing.references.push(...references);
    } else {
      sourceArtifactsByDocument.set(documentKey, {
        document: toCitationDocument(part),
        references: [...references],
      });
    }
  }

  const sourceArtifacts = Array.from(sourceArtifactsByDocument.entries()).map(([documentKey, entry]) => {
    const seen = new Set<string>();
    const references = entry.references.filter((reference) => {
      if (seen.has(reference.referenceId)) {
        return false;
      }
      seen.add(reference.referenceId);
      return true;
    });

    return {
      document: entry.document,
      key: `${message.id}-${documentKey}`,
      reference: references[0] ?? null,
      references,
    };
  });

  useEffect(() => {
    if (message.role !== 'assistant') {
      return;
    }

    if (visualState === 'activity' || visualState === 'content') {
      markChatAssistantActivityVisible();
    }

    if (textContent.length > 0) {
      markChatFirstTextVisible();
    }

    // Garde anti-régression : un message assistant réglé (hors stream) qui ne
    // rend rien de visible est une perte de contenu silencieuse à mesurer.
    if (visualState === 'empty') {
      countChatUxEvent('chat.ui.blank_assistant_state_count');
    }
  }, [message.role, textContent.length, visualState]);

  if (message.role === 'assistant' && (visualState === 'empty' || visualState === 'thinking')) {
    return null;
  }

  const shouldRenderActions = isUser || textContent.length > 0;

  return (
    <Message
      from={message.role}
      className={`max-w-[min(100%,48rem)] ${isUser ? 'items-end' : 'items-start'}`}
    >
      <MessageContent
        className={[
          'w-fit px-0 py-0 shadow-none',
          isUser
            ? 'ml-auto max-w-[min(70%,26rem)] !rounded-[1.5rem] bg-secondary px-5 !py-2 text-foreground'
            : 'border-transparent bg-transparent text-foreground',
        ].join(' ')}
      >
        <div className="flex flex-col gap-3.5">
            {fileParts.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {fileParts.map((part) => (
                  <button
                    type="button"
                    key={`${message.id}-${part.attachmentId ?? part.url ?? part.filename ?? 'file'}`}
                    onClick={() => openDocument({
                      content: '',
                      documentId: part.attachmentId ?? part.url ?? `${message.id}-file`,
                      filename: part.filename ?? null,
                      kind: 'attachment',
                      mediaType: part.mediaType,
                      sourceUrl: part.url ?? (part.attachmentId
                        ? new URL(`/api/chat/attachments/${part.attachmentId}`, apiBaseUrl).toString()
                        : null),
                      status: 'ready',
                      summary: part.filename ?? part.mediaType,
                      title: part.filename ?? 'Fichier joint',
                    })}
                    className={[
                      'inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-medium transition',
                      isUser
                        ? 'border border-border/60 bg-background text-current hover:bg-background/80'
                        : 'border border-border/70 bg-muted/35 text-foreground hover:bg-muted/55',
                    ].join(' ')}
                  >
                    <FileIcon className="size-3.5" />
                    <span className="max-w-44 truncate">{part.filename ?? 'Fichier joint'}</span>
                  </button>
                ))}
              </div>
            ) : null}

            {reasoningContent ? (
              <Reasoning
                className="mb-0 w-full"
                defaultOpen={false}
                isStreaming={isReasoningStreaming}
                startedAt={reasoningStartedAt}
              >
                <ReasoningTrigger />
                <ReasoningContent>{reasoningContent}</ReasoningContent>
              </Reasoning>
            ) : null}

            {webResearchActivityParts.length > 0 || webActivityToolParts.length > 0 ? (
              <ChatWebActivity
                activities={webResearchActivityParts.map((part) => part.data)}
                isStreaming={isLoading}
                toolParts={webActivityToolParts}
              />
            ) : null}

            {textContent ? (
              isUser ? (
                <p className="whitespace-pre-wrap text-base leading-7">{textContent}</p>
              ) : (
                <div className="chat-markdown max-w-none text-base leading-7 text-foreground">
                  <MessageResponse isAnimating={Boolean(isLoading) || isRevealing}>
                    {revealedTextContent}
                  </MessageResponse>
                </div>
              )
            ) : null}

            {askUserToolParts.length > 0 ? (
              <div className="flex flex-col gap-3 overflow-visible">
                {askUserToolParts.map((toolPart, toolIndex) => (
                  <ChatAskUserTool
                    key={`${message.id}-ask-user-${toolPart.toolCallId}-${toolIndex}`}
                    toolPart={toolPart}
                  />
                ))}
              </div>
            ) : null}

            {docgenPendingParts.length > 0 && documentGenerationToolParts.length === 0 ? (
              <div className="flex items-center text-sm" data-testid="docgen-preparing">
                <Shimmer>
                  {docgenPendingParts[0]?.data.title
                    ? `Préparation du document « ${docgenPendingParts[0].data.title} »…`
                    : 'Préparation du document…'}
                </Shimmer>
              </div>
            ) : null}

            {documentGenerationToolParts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {documentGenerationToolParts.map((toolPart, toolIndex) => (
                  <ChatDocumentGenerationTool
                    errorText={toolPart.errorText}
                    input={toolPart.input}
                    key={`${message.id}-${toolPart.type}-${toolIndex}`}
                    output={toolPart.output}
                    state={toolPart.state}
                  />
                ))}
              </div>
            ) : null}

            {genericToolParts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {genericToolParts.map((toolPart, toolIndex) => (
                  <Tool key={`${message.id}-${toolPart.type}-${toolIndex}`}>
                    <ToolHeader
                      state={toolPart.state}
                      title={toolPart.title}
                      toolName={getToolName(toolPart)}
                    />
                    <p className="border-t border-border/50 px-3 py-3 text-xs text-muted-foreground">
                      {getSafeToolStatusMessage(toolPart.state)}
                    </p>
                  </Tool>
                ))}
              </div>
            ) : null}

            {documentParts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {documentParts.map((documentPart) => (
                  <ChatDocumentCard
                    key={`${message.id}-${documentPart.data.documentId}`}
                    document={documentPart.data}
                    onOpen={openDocument}
                  />
                ))}
              </div>
            ) : null}

            {ragEmptyParts.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {ragEmptyParts.map((part) => (
                  <Button
                    asChild
                    variant="outline"
                    key={`${message.id}-${part.id ?? part.data.actionHref}`}
                  >
                    <Link to={part.data.actionHref}>
                      <FilePlusIcon className="size-4" />
                      <span>{part.data.actionLabel}</span>
                    </Link>
                  </Button>
                ))}
              </div>
            ) : null}

            {webUrlSourceParts.length > 0 ? (
              <div className="not-prose flex flex-col gap-2 text-xs text-primary">
                <p className="font-medium">
                  {webUrlSourceParts.length > 1
                    ? `${webUrlSourceParts.length} sources web consultées`
                    : '1 source web consultée'}
                </p>
                <div className="flex flex-col gap-2">
                  {webUrlSourceParts.map((part) => (
                    <Source
                      key={`${message.id}-${part.sourceId}`}
                      href={part.url}
                      title={part.title ?? part.url}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {sourceArtifacts.length > 0 ? (
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => {
                    setAreSourcesVisible((current) => !current);
                  }}
                  className="rounded-full border border-border/70 bg-muted/25 px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted/45"
                >
                  {areSourcesVisible
                    ? `Masquer les sources (${sourceArtifacts.length})`
                    : `Voir les sources (${sourceArtifacts.length})`}
                </button>
              </div>
            ) : null}

            {sourceArtifacts.length > 0 && areSourcesVisible ? (
              <div className="grid max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
                {sourceArtifacts.map((artifact) => (
                  <ChatSourceCard
                    key={artifact.key}
                    document={artifact.document}
                    sourceReference={artifact.reference}
                    sourceReferences={artifact.references}
                    onOpen={openDocument}
                  />
                ))}
              </div>
            ) : null}

            {redactedSourceParts.length > 0 ? (
              <div className="text-xs text-muted-foreground" role="status">
                {redactedSourceParts.map((part) => (
                  <p key={`${message.id}-${part.sourceId}-redacted`}>
                    {(part as Record<string, unknown>).redactedLabel as string || 'Source non accessible'}
                  </p>
                ))}
              </div>
            ) : null}
        </div>
      </MessageContent>

      {shouldRenderActions ? (
        <MessageToolbar className={isUser ? 'justify-end px-0.5 pt-1' : 'justify-start px-0.5 pt-1'}>
          <ChatMessageActions
            canRetry={canRetry}
            message={message}
            onEdit={onEditMessage}
            onRetry={onRetryAssistant}
          />
        </MessageToolbar>
      ) : null}
    </Message>
  );
}

export const MessageBubble = memo(MessageBubbleComponent, areMessageBubblePropsEqual);
